import { ContactLinkTypes, ICartItem, ICartOption, IDeliveryOrderData, IOrderParams, IPreOrderData, IReserveOrderData } from "../Interfaces/componentTypes";
import { ContactLinkResult, IBusinessProfile, IOrderResponse } from "../Interfaces/responseTypes";
import { InvoiceLangTypes, PosPaperSizes } from "../Pages/Business/Restaurant/Components/Dialogs/PrintInvoice/printInvoice-hook";
interface ExtendedNotificationOptions extends NotificationOptions {
  renotify?: boolean;
  vibrate: number[]
}
export const isBusinessOpen = (profile: IBusinessProfile): boolean => {
  const now = new Date();
  const currentDay = now.getDay() === 0 ? 7 : now.getDay();
  const currentTime = now.getHours() * 60 + now.getMinutes();
  if (profile.free_days.includes(currentDay)) return false;

  if (profile.is_24) return true;

  if (!profile.start_time || !profile.end_time) return false;

  const [startHour, startMin] = profile.start_time.split(':').map(Number);
  const [endHour, endMin] = profile.end_time.split(':').map(Number);
  const startTime = startHour * 60 + startMin;
  const endTime = endHour * 60 + endMin;

  return currentTime >= startTime && currentTime <= endTime;
};

export const generateContactLink = (
  type: ContactLinkTypes,
  rawValue: string | null
): ContactLinkResult | null => {
  if (!rawValue) return null;

  const trim = (val: string) => val.trim();
  const stripPlus = (val: string) => val.replace(/^\+/, "");
  const stripNonDigits = (val: string) => val.replace(/\D/g, "");
  const stripAt = (val: string) => val.replace(/^@/, "");

  const cleaned = trim(rawValue);

  let href = "";
  let fallback = "";

  switch (type) {
    case "facebook": {
      let url = cleaned;

      const isFullUrl = /^https?:\/\//i.test(url);
      const isDomainOnly = /^facebook\.com\//i.test(url);
      const fullUrl = isFullUrl
        ? url
        : isDomainOnly
          ? `https://${url}`
          : `https://facebook.com/${url}`;

      fallback = fullUrl;
      href = fullUrl; // fb:// deep links are unreliable
      break;
    }

    case "instagram": {
      const username = stripAt(cleaned);
      href = `instagram://user?username=${username}`;
      fallback = `https://instagram.com/${username}`;
      break;
    }

    case "telegram": {
      const username = stripAt(cleaned);
      href = `tg://resolve?domain=${username}`;
      fallback = `https://t.me/${username}`;
      break;
    }

    case "whatsapp": {
      const phone = stripNonDigits(stripPlus(cleaned));
      href = `whatsapp://send?phone=${phone}`;
      fallback = `https://wa.me/${phone}`;
      break;
    }

    case "viber": {
      const phone = stripNonDigits(stripPlus(cleaned));
      href = `viber://chat?number=${phone}`;
      fallback = `https://invite.viber.com/?number=${phone}`;
      break;
    }

    case "twitter": {
      const username = stripAt(cleaned);
      href = `twitter://user?screen_name=${username}`;
      fallback = `https://twitter.com/${username}`;
      break;
    }

    default:
      return null;
  }

  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  const open = () => {
    if (isMobile) {
      const timeout = setTimeout(() => {
        window.open(fallback, "_blank");
      }, 800);

      window.location.href = href;

      window.addEventListener("blur", () => clearTimeout(timeout));
    } else {
      window.open(fallback, "_blank");
    }
  };

  return { href, fallback, open };
};


export const formatPercent = (value: string): string => {
  const num = parseFloat(value);
  if (isNaN(num)) return '';
  const fixed = num.toFixed(2);
  const cleaned = parseFloat(fixed).toString();
  return cleaned;
};

export function getTotalCartItemCount(cart: ICartItem[]): number {
  if (cart.length === 0) {
    return 0;
  }

  return cart.reduce((total: number, item: ICartItem) => {
    const optionCount = Array.isArray(item.option)
      ? item.option.reduce((optionTotal: number, opt: ICartOption) => optionTotal + opt.count, 0)
      : 0;

    return total + optionCount;
  }, 0);
};

export function formatDate(dateInput: string | Date, withTime: boolean = true): string {
  let date: Date;

  if (dateInput instanceof Date) {
    date = dateInput;
  } else if (typeof dateInput === 'string') {
    // Detect DD-MM-YYYY HH:mm
    const match = dateInput.match(/^(\d{2})-(\d{2})-(\d{4})[ T](\d{2}):(\d{2})$/);
    if (match) {
      const [, day, month, year, hour, minute] = match.map(Number);
      date = new Date(year, month - 1, day, hour, minute);
    } else {
      // Let JS try normal parsing (ISO, RFC 2822, etc.)
      date = new Date(dateInput);
    }
  } else {
    return '';
  }

  if (isNaN(date.getTime())) return '';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  if (!withTime) {
    return `${day}.${month}.${year}`;
  }

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}.${month}.${year} ${hours}:${minutes}`;
};


export function formatTime(dateInput: string | Date): string {
  if (!dateInput) return '';

  const date = new Date(dateInput);
  const hours = date.getHours().toString().padStart(2, '0'); // local hours
  const minutes = date.getMinutes().toString().padStart(2, '0'); // local minutes
  return `${hours}:${minutes}`;
};


export function printInvoice(
  order: IOrderResponse,
  language: InvoiceLangTypes,
  paperWidth: PosPaperSizes,
  QR: string
) {
  const orderParams: IOrderParams = JSON.parse(order.order_parameters);
  const orderData: IDeliveryOrderData | IPreOrderData | IReserveOrderData =
    JSON.parse(order.data);
  const baseURL = process.env.REACT_APP_API_KEY;

  // --- Translations ---
  const t = {
    en: {
      order: "Order",
      orderCreated: "Order Created Date",
      scheduled: "Scheduled Date",
      phone: "Phone",
      address: "Address",
      persons: "Persons",
      deliveryFee: "Delivery Fee",
      packageFee: "Package Fee",
      serviceFee: "Service fee",
      total: "TOTAL",
      service: "Service provided by www.mrbyte.am",
      thank: "Thank you!",
      print: "Print",
      close: "Close",
    },
    ru: {
      order: "Заказ",
      orderCreated: "Дата создания заказа",
      scheduled: "Запланированная дата",
      phone: "Телефон",
      address: "Адрес",
      persons: "Персон",
      deliveryFee: "Стоимость доставки",
      packageFee: "Стоимость упаковки",
      serviceFee: "Комиссия за обслуживание",
      total: "ИТОГО",
      service: "Сервис предоставлен www.mrbyte.am",
      thank: "Спасибо!",
      print: "Печать",
      close: "Закрыть",
    },
    hy: {
      order: "Պատվեր",
      orderCreated: "Պատվերի ստեղծման օր",
      scheduled: "Նշված օր",
      phone: "Հեռ.",
      address: "Հասցե",
      persons: "Անձերի քանակ",
      deliveryFee: "Առաքման վճար",
      packageFee: "Փաթեթավորման վճար",
      serviceFee: "Սպասարկման վճար",
      total: "ԱՄԲՈՂՋ",
      service: "Ծառայությունը տրամադրված է www.mrbyte.am կողմից",
      thank: "Շնորհակալություն!",
      print: "Տպել",
      close: "Փակել",
    },
  }[language];

  // Group cart
  const groupedCart: Record<string, ICartItem[]> = {};
  orderData.cart.forEach((item) => {
    const categoryName =
      (item.category[`name_${language}` as keyof typeof item.category] as string) ||
      item.category.name_en;
    if (!groupedCart[categoryName]) groupedCart[categoryName] = [];
    groupedCart[categoryName].push(item);
  });

  // Helpers
  const win = window.open("", "_blank", `width=420,height=600`);
  if (!win) return;
  const doc = win.document;
  doc.title = "Invoice";

  // --- Styles ---
  const style = doc.createElement("style");
  style.textContent = `
    body { font-family: monospace; margin:0; padding:8px; background:#fff; color:#000; }
    .paper { width: ${paperWidth}mm; margin:0 auto; padding:6px 8px; border:1px dashed #ddd; position:relative; }
    .header { text-align:center; font-weight:bold; font-size:14px; margin-bottom:2px; }
    .contacts { text-align:center; font-size:11px; margin-bottom:6px; }
    .category { font-weight:bold; border-bottom:1px solid #000; margin:6px 0 4px; }
    .item-row {
      display:flex; justify-content:space-between; align-items:flex-start;
      font-size:${paperWidth === 58 ? 11 : 12}px; line-height:1.4; margin:2px 0;
    }
    .fee-box {
      border-top:1px solid #000;
      margin-top:6px; padding-top:4px;
      display:flex; justify-content:space-between;
      font-size:${paperWidth === 58 ? 11 : 12}px; font-weight:bold;
    }
    .item-name { flex:1; text-align:left; word-wrap:break-word; white-space:normal; }
    .item-qty { min-width:30px; text-align:center; }
    .item-price { min-width:60px; text-align:right; }
    .total-row {
      border-top:2px solid #000;
      margin-top:8px; padding-top:4px;
      display:flex; justify-content:space-between; font-weight:bold;
      font-size:${paperWidth === 58 ? 12 : 13}px;
    }
    .company-logo {
      display:block;
      max-width:120px;
      max-height:60px;
      margin:8px auto;
      object-fit:contain;
    }
    .footer-box {
      text-align:center; border:1px solid #000; padding:4px; margin-top:8px;
      font-size:11px; font-weight:bold;
    }
    .thankyou { text-align:center; margin-top:4px; font-weight:bold; }
    .actions {
      position:fixed; bottom:0; left:0; right:0;
      background:#f8f8f8; padding:10px; display:flex; gap:10px; justify-content:center;
      border-top:1px solid #ccc;
    }
    .actions button {
      flex:1; max-width:120px;
      padding:8px 12px; font-weight:bold;
      border:none; border-radius:6px; cursor:pointer;
      background:#222; color:#fff; transition:0.2s;
    }
    .actions button:hover { background:#555; }
    @media print { .actions { display:none; } body { padding:0; } }
  `;
  doc.head.appendChild(style);

  // --- Content ---
  const paperDiv = doc.createElement("div");
  paperDiv.className = "paper";

  // Company header
  if (orderParams.company_logo) {
    const logoImg = doc.createElement("img");
    logoImg.className = "company-logo";
    logoImg.src = `${baseURL}${orderParams.company_logo}`;
    paperDiv.appendChild(logoImg);
  }

  const headerDiv = doc.createElement("div");
  headerDiv.className = "header";
  headerDiv.textContent = orderParams.company_name || "Company";
  paperDiv.appendChild(headerDiv);

  // Contacts
  if (orderParams.company_contacts && orderParams.company_contacts.length > 0) {
    const contactsDiv = doc.createElement("div");
    contactsDiv.className = "contacts";
    contactsDiv.textContent = orderParams.company_contacts.join(" | ");
    paperDiv.appendChild(contactsDiv);
  }

  // Order info
  const orderInfo = doc.createElement("div");
  orderInfo.innerHTML = `
    <div>${t.order} #${order.id}</div>
    <div>${t.orderCreated}: ${formatDate(String(order.created_at))}</div>
    ${orderParams.time
      ? `<div>${t.scheduled}: ${formatDate(String(orderParams.time))}</div>`
      : ""
    }
    ${orderParams.phone_number ? `<div>${t.phone}: ${orderParams.phone_number}</div>` : ""}
    ${orderParams.address ? `<div>${t.address}: ${orderParams.address}</div>` : ""}
    ${orderParams.persons_count || orderParams.persons_count
      ? `<div>${t.persons}: ${orderParams.persons_count ?? orderParams.persons_count ?? "1"}</div>`
      : ""
    }
  `;
  paperDiv.appendChild(orderInfo);

  // Cart
  for (const category in groupedCart) {
    const catDiv = doc.createElement("div");
    catDiv.className = "category";
    catDiv.textContent = category.toUpperCase();
    paperDiv.appendChild(catDiv);

    groupedCart[category].forEach((item) => {
      const itemName =
        (item[`name_${language}` as keyof ICartItem] as string) || item.name_en;
      const quantity =
        (item.option || []).reduce((a, o) => a + (o.count ?? 1), 0) || 1;
      const price = (item.option || []).reduce(
        (a, o) => a + Number(o.price ?? 0) * (o.count ?? 1),
        0
      );

      const itemRow = doc.createElement("div");
      itemRow.className = "item-row";
      itemRow.innerHTML = `
        <span class="item-name">${itemName}</span>
        <span class="item-qty">x${quantity}</span>
        <span class="item-price">${price} ֏</span>
      `;
      paperDiv.appendChild(itemRow);

      if (item.note) {
        const noteDiv = doc.createElement("div");
        noteDiv.style.fontSize = "10px";
        noteDiv.style.marginLeft = "8px";
        noteDiv.textContent = `- ${item.note}`;
        paperDiv.appendChild(noteDiv);
      }
    });
  }

  // Delivery + Package Fees
  if ("delivery_cost" in orderData) {
    const row = doc.createElement("div");
    row.className = "fee-box";
    row.innerHTML = `<span>${t.deliveryFee}</span><span>${(orderData as IDeliveryOrderData).delivery_cost
      } ֏</span>`;
    paperDiv.appendChild(row);
  }

  if ("packageFee" in orderData && (orderData as IDeliveryOrderData | IPreOrderData).packageFee) {
    const row = doc.createElement("div");
    row.className = "fee-box";
    row.innerHTML = `<span>${t.packageFee}</span><span>${(orderData as IDeliveryOrderData | IPreOrderData).packageFee
      } ֏</span>`;
    paperDiv.appendChild(row);
  }

  if ("service_fee " in orderData && (orderData as IReserveOrderData).service_fee) {
    const row = doc.createElement("div");
    row.className = "fee-box";
    row.innerHTML = `<span>${t.serviceFee}</span><span>${(orderData as IReserveOrderData).service_fee
      } %</span>`;
    paperDiv.appendChild(row);
  }

  // Total row
  const totalRow = doc.createElement("div");
  totalRow.className = "total-row";
  if (orderData.cart?.length > 0) {
    totalRow.innerHTML = `<span>${t.total}</span><span>${orderData.total_cost} ֏</span>`;
  }
  paperDiv.appendChild(totalRow);

  // --- Logo before footer ---
  if (QR) {
    const qrImg = doc.createElement("img");
    qrImg.className = "company-logo";
    qrImg.src = `${baseURL}${QR}`;
    paperDiv.appendChild(qrImg);
  }

  // Footer
  const footerBox = doc.createElement("div");
  footerBox.className = "footer-box";
  footerBox.textContent = t.service;
  paperDiv.appendChild(footerBox);

  const thankDiv = doc.createElement("div");
  thankDiv.className = "thankyou";
  thankDiv.textContent = t.thank;
  paperDiv.appendChild(thankDiv);

  doc.body.appendChild(paperDiv);

  // Action buttons
  const actionsDiv = doc.createElement("div");
  actionsDiv.className = "actions";
  const printBtn = doc.createElement("button");
  printBtn.textContent = t.print;
  printBtn.onclick = () => win.print();
  const closeBtn = doc.createElement("button");
  closeBtn.textContent = t.close;
  closeBtn.onclick = () => win.close();
  actionsDiv.appendChild(printBtn);
  actionsDiv.appendChild(closeBtn);
  doc.body.appendChild(actionsDiv);
};

// src/utils/notifications.ts
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!("Notification" in window)) return "denied";
  if (Notification.permission !== "default") return Notification.permission;
  try {
    const res = await Notification.requestPermission();
    return res;
  } catch {
    return "denied";
  }
};

export const getSWRegistration = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.ready;
  } catch {
    return null;
  }
};

// Show a system notification via the Service Worker (works even when tab is backgrounded)
export const showLocalNotification = (
  title: string,
  body: string,
  swRegistration: ServiceWorkerRegistration
) => {
  const options: ExtendedNotificationOptions = {
    body,
    icon: "/icon.png",
    vibrate: [200, 100, 200],
    renotify: true, // ✅ now TypeScript won’t complain
    tag: "notification-tag"
  };

  swRegistration.showNotification(title, options);
};

export const isNotificationPermissionGranted = (): boolean => {
  return "Notification" in window && Notification.permission === "granted";
};

export const isNotificationPermissionDenied = (): boolean => {
  return "Notification" in window && Notification.permission === "denied";
};
